import React, { useMemo, useState, useEffect } from 'react';
import IconButton from '../../icons/IconButton';
import IconText from '../../icons/IconText';
import { ICON } from '../../../icons/iconIds';
import SelkirkIcon from '../../icons/SelkirkIcon';
import { useAutoSelectSingleFilterMatch } from '../../../utils/qaqc/useAutoSelectSingleFilterMatch';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ScatterChart,
} from 'recharts';
import { 
  transformControlStandardsForChart, 
  getUniqueCRMCodes,
  COLORS
} from '../../../utils/qaqc/chartDataTransform';
import {
  augmentRowsZScorePlot,
  boundsForAssayValues,
  augmentRowsNumericPlot,
  Z_SCORE_AXIS_MAX,
  Z_SCORE_AXIS_MIN
} from '../../../utils/qaqc/chartOutliers';

const ControlStandardsChart = ({ 
  data, 
  selectedElement = 'Cu',
  selectedCRM = 'all',
  onElementChange,
  onCRMChange,
  onPointClick,
  externalSampleFilter = null,
  height = 400
}) => {
  const [showValues, setShowValues] = useState(false); // Toggle between z-score and actual values
  const [sampleFilter, setSampleFilter] = useState('');

  useEffect(() => {
    if (externalSampleFilter == null || externalSampleFilter === '') return;
    setSampleFilter(String(externalSampleFilter));
  }, [externalSampleFilter]);
  
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformControlStandardsForChart(data, selectedElement, selectedCRM);
  }, [data, selectedElement, selectedCRM]);
  
  // Filter chart data by sample number
  const filteredChartData = useMemo(() => {
    if (!sampleFilter) return chartData;
    const term = sampleFilter.toLowerCase();
    return chartData.filter(d =>
      d.sampleNumber?.toLowerCase().includes(term) ||
      d.workorderId?.toLowerCase().includes(term)
    );
  }, [chartData, sampleFilter]);

  useAutoSelectSingleFilterMatch(sampleFilter, filteredChartData, onPointClick);
  
  // Get unique CRM codes for filter
  const crmCodes = useMemo(() => {
    return getUniqueCRMCodes(data.filter(item => item.element === selectedElement));
  }, [data, selectedElement]);
  
  // Calculate statistics (use filtered data)
  const stats = useMemo(() => {
    if (filteredChartData.length === 0) return null;
    
    const zScores = filteredChartData.map(d => d.zScore);
    const mean = zScores.reduce((sum, z) => sum + z, 0) / zScores.length;
    const variance = zScores.reduce((sum, z) => sum + Math.pow(z - mean, 2), 0) / zScores.length;
    const stddev = Math.sqrt(variance);
    
    const failures = filteredChartData.filter(d => !d.within3sd).length;
    const warnings = filteredChartData.filter(d => !d.within2sd && d.within3sd).length;
    const passes = filteredChartData.filter(d => d.within2sd).length;
    
    return {
      count: filteredChartData.length,
      mean: mean.toFixed(3),
      stddev: stddev.toFixed(3),
      failures,
      warnings,
      passes,
      passRate: ((passes / filteredChartData.length) * 100).toFixed(1)
    };
  }, [filteredChartData]);

  const plotChartData = useMemo(() => {
    if (showValues) {
      const vals = filteredChartData
        .map((d) => d.bestValue)
        .filter((v) => typeof v === 'number' && !Number.isNaN(v));
      const { lo, hi } = boundsForAssayValues(vals);
      return augmentRowsNumericPlot(filteredChartData, 'bestValue', lo, hi, (v) =>
        v.toFixed(4)
      );
    }
    return augmentRowsZScorePlot(filteredChartData, 'zScore');
  }, [filteredChartData, showValues]);

  const valueAxisDomain = useMemo(() => {
    if (!showValues || plotChartData.length === 0) return null;
    const vals = plotChartData
      .map((d) => d.plotBestValue)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (vals.length === 0) return ['auto', 'auto'];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.04 || 0.01;
    return [lo - pad, hi + pad];
  }, [plotChartData, showValues]);
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      
      return (
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          padding: '12px',
          color: '#f9fafb',
          fontSize: '0.875rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#06b6d4' }}>
            {data.sampleNumber}
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#9ca3af', marginBottom: '8px' }}>
            {data.crmCode} | {data.workorderId}
            {data.receivedDate ? ` | Received: ${String(data.receivedDate).slice(0, 10)}` : ''}
          </div>
          <div style={{ borderTop: '1px solid #374151', paddingTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Measured:</span>
              <span style={{ fontWeight: 'bold' }}>{data.bestValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Certified:</span>
              <span style={{ fontWeight: 'bold', color: COLORS.certified }}>{data.certifiedValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Difference:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: Math.abs(data.diffFromCert) > data.stdDev * 2 ? COLORS.warning : COLORS.text
              }}>
                {data.diffFromCert > 0 ? '+' : ''}{data.diffFromCert.toFixed(4)}
              </span>
            </div>
            <div style={{ borderTop: '1px solid #374151', paddingTop: '4px', marginTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                <span>Z-Score:</span>
                <span style={{ 
                  fontWeight: 'bold',
                  color: Math.abs(data.zScore) > 3 ? COLORS.fail : 
                         Math.abs(data.zScore) > 2 ? COLORS.warning : COLORS.pass
                }}>
                  {data.zScore.toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
                Status: <span style={{ 
                  fontWeight: 'bold',
                  color: data.status === 'fail' ? COLORS.fail : 
                         data.status === 'warning' ? COLORS.warning : COLORS.pass
                }}>
                  {data.status === 'fail' ? (
                    <IconText icon={ICON.uiError} iconSize={14}>FAIL (&gt;3SD)</IconText>
                  ) : data.status === 'warning' ? (
                    <IconText icon={ICON.uiWarning} iconSize={14}>WARNING (&gt;2SD)</IconText>
                  ) : (
                    <IconText icon={ICON.uiCheckOn} iconSize={14}>PASS</IconText>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
  
  // Custom dot for scatter points (colored by status)
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (!payload) return null;
    
    const color = payload.status === 'fail' ? COLORS.fail :
                  payload.status === 'warning' ? COLORS.warning : COLORS.pass;
    
    const size = payload.status === 'fail' ? 8 : 
                 payload.status === 'warning' ? 6 : 5;

    const clipLabel = showValues
      ? payload.bestValueAxisLabel
      : payload.zScoreAxisLabel;
    const labelBelow = clipLabel && (
      showValues
        ? payload.bestValue >
          (payload.plotBestValue ?? payload.bestValue)
        : payload.zScore > (payload.plotZScore ?? payload.zScore)
    );
    
    return (
      <g>
        <circle 
          cx={cx} 
          cy={cy} 
          r={size} 
          fill={color}
          stroke={payload.status === 'fail' ? '#fff' : color}
          strokeWidth={payload.status === 'fail' ? 2 : 1}
          style={{ cursor: onPointClick ? 'pointer' : 'default' }}
          onClick={() => onPointClick && onPointClick(payload.raw)}
        />
        {clipLabel && (
          <text
            x={cx}
            y={labelBelow ? cy + size + 11 : cy - size - 4}
            fill="#e5e7eb"
            fontSize={10}
            textAnchor="middle"
          >
            {clipLabel}
          </text>
        )}
      </g>
    );
  };
  
  // Render no data message (used when no raw data at all)
  const renderNoRawData = () => (
    <div style={{ 
      padding: '3rem', 
      textAlign: 'center', 
      color: '#9ca3af',
      background: '#1f2937',
      borderRadius: '8px',
      border: '1px solid #374151',
      marginTop: '1rem'
    }}>
      <div className="no-data-icon"><SelkirkIcon name={ICON.uiChartAnalytics} size={48} decorative /></div>
      <h3 style={{ color: '#f9fafb', marginBottom: '0.5rem' }}>No Control Standards Data</h3>
      <p style={{ fontSize: '0.875rem' }}>Data will appear here once control standards are analyzed.</p>
    </div>
  );
  
  // Render no filtered data message (used when filters result in empty dataset)
  const renderNoFilteredData = () => (
    <div style={{ 
      padding: '3rem', 
      textAlign: 'center', 
      color: '#9ca3af',
      background: '#1f2937',
      borderRadius: '8px',
      border: '1px solid #374151',
      marginTop: '1rem'
    }}>
      <div className="no-data-icon"><SelkirkIcon name={ICON.uiSearch} size={48} decorative /></div>
      <h3 style={{ color: '#f9fafb', marginBottom: '0.5rem' }}>No Data for Selected Filters</h3>
      <p style={{ fontSize: '0.875rem' }}>
        Try selecting a different element or CRM type{sampleFilter ? ' or adjust your sample filter' : ''}.
      </p>
      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
        Current filters: <strong style={{ color: '#f9fafb' }}>{selectedElement}</strong>
        {selectedCRM !== 'all' && <> • <strong style={{ color: '#f9fafb' }}>{selectedCRM}</strong></>}
        {sampleFilter && <> • Sample: <strong style={{ color: '#f9fafb' }}>{sampleFilter}</strong></>}
      </p>
    </div>
  );
  
  // Check if we have any raw data at all
  if (!data || data.length === 0) {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#f9fafb' }}>
            <IconText icon={ICON.qaqcDomainControlStandard} iconSize={18} as="span">Control Standards</IconText>
          </h3>
        </div>
        {renderNoRawData()}
      </div>
    );
  }
  
  return (
    <div style={{ width: '100%' }}>
      {/* Header with filters - ALWAYS VISIBLE */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '1rem', 
          fontWeight: '600', 
          color: '#f9fafb' 
        }}>
          <IconText icon={ICON.qaqcDomainControlStandard} iconSize={18} as="span">Control Standards</IconText> - {selectedElement} {showValues ? 'Values' : 'Z-Scores'}
        </h3>
        
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Element selector */}
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {['Au', 'Ag', 'Cu'].map(element => (
              <button
                key={element}
                onClick={() => onElementChange && onElementChange(element)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: selectedElement === element ? '#06b6d4' : '#374151',
                  background: selectedElement === element ? 'rgba(6, 182, 212, 0.1)' : '#1f2937',
                  color: selectedElement === element ? '#06b6d4' : '#9ca3af',
                  fontSize: '0.875rem',
                  fontWeight: selectedElement === element ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {element}
              </button>
            ))}
          </div>
          
          {/* CRM selector */}
          {crmCodes.length > 0 && (
            <select
              value={selectedCRM}
              onChange={(e) => onCRMChange && onCRMChange(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid #374151',
                background: '#1f2937',
                color: '#f9fafb',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All CRMs</option>
              {crmCodes.map(crm => (
                <option key={crm} value={crm}>{crm}</option>
              ))}
            </select>
          )}
          
          {/* Sample Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Sample Filter:</label>
            <input
              type="text"
              placeholder="Search by sample number..."
              value={sampleFilter}
              onChange={(e) => setSampleFilter(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem',
                minWidth: '200px'
              }}
            />
            {sampleFilter && (
              <IconButton
                icon={ICON.ddClose}
                onClick={() => setSampleFilter('')}
                ariaLabel="Clear sample filter"
              />
            )}
          </div>
          
          {/* Toggle view */}
          <button
            onClick={() => setShowValues(!showValues)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: '1px solid #374151',
              background: '#1f2937',
              color: '#9ca3af',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {showValues ? (<IconText icon={ICON.uiChartAnalytics} iconSize={14}>Show Z-Scores</IconText>) : (<IconText icon={ICON.uiChartAnalytics} iconSize={14}>Show Values</IconText>)}
          </button>
        </div>
      </div>
      
      {/* Conditional rendering: Show message OR chart */}
      {chartData.length === 0 ? (
        renderNoFilteredData()
      ) : (
        <>
      
      {/* Statistics summary */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '0.75rem',
          background: '#111827',
          borderRadius: '6px',
          border: '1px solid #374151'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#06b6d4' }}>
              {stats.count}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Samples</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.pass }}>
              {stats.passes}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Pass</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.warning }}>
              {stats.warnings}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Warning</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.fail }}>
              {stats.failures}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Fail</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f9fafb' }}>
              {stats.passRate}%
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Pass Rate</div>
          </div>
        </div>
      )}
      
      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
          
          <XAxis 
            type="number"
            dataKey="index"
            stroke={COLORS.text}
            tick={{ fill: COLORS.text, fontSize: 11 }}
            label={{ 
              value: 'Sample Order (by Date)', 
              position: 'insideBottom', 
              offset: -10,
              style: { fill: COLORS.text, fontSize: 12 }
            }}
          />
          
          <YAxis 
            type="number"
            stroke={COLORS.text}
            tick={{ fill: COLORS.text, fontSize: 11 }}
            label={{ 
              value: showValues ? `${selectedElement} Value` : 'Z-Score', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: COLORS.text, fontSize: 12 }
            }}
            domain={
              showValues
                ? valueAxisDomain || ['auto', 'auto']
                : [Z_SCORE_AXIS_MIN, Z_SCORE_AXIS_MAX]
            }
            allowDataOverflow
            tickFormatter={(value) => showValues ? value.toFixed(4) : value.toFixed(2)}
          />
          
          {!showValues && (
            <>
              {/* Reference lines for Z-scores */}
              <ReferenceLine 
                y={0} 
                stroke={COLORS.pass} 
                strokeWidth={2}
                label={{ value: 'Target', fill: COLORS.pass, fontSize: 10 }}
              />
              <ReferenceLine 
                y={2} 
                stroke={COLORS.warning} 
                strokeDasharray="5 5"
                label={{ value: '+2SD', fill: COLORS.warning, fontSize: 10, position: 'right' }}
              />
              <ReferenceLine 
                y={-2} 
                stroke={COLORS.warning} 
                strokeDasharray="5 5"
                label={{ value: '-2SD', fill: COLORS.warning, fontSize: 10, position: 'right' }}
              />
              <ReferenceLine 
                y={3} 
                stroke={COLORS.fail} 
                strokeDasharray="3 3"
                strokeWidth={2}
                label={{ value: '+3SD', fill: COLORS.fail, fontSize: 10, position: 'right' }}
              />
              <ReferenceLine 
                y={-3} 
                stroke={COLORS.fail} 
                strokeDasharray="3 3"
                strokeWidth={2}
                label={{ value: '-3SD', fill: COLORS.fail, fontSize: 10, position: 'right' }}
              />
            </>
          )}
          
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
          
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            content={() => (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '1.5rem',
                fontSize: '0.875rem',
                color: '#9ca3af'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    background: COLORS.pass 
                  }} />
                  Pass (±2SD)
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    background: COLORS.warning 
                  }} />
                  Warning (>2SD)
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    background: COLORS.fail,
                    border: '2px solid #fff'
                  }} />
                  Fail (>3SD)
                </span>
              </div>
            )}
          />
          
          <Scatter 
            data={plotChartData}
            dataKey={showValues ? 'plotBestValue' : 'plotZScore'}
            shape={<CustomDot />}
          />
        </ScatterChart>
      </ResponsiveContainer>
      
      {/* Helper text */}
      {onPointClick && (
        <div style={{ 
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '6px',
          fontSize: '0.875rem', 
          color: '#9ca3af', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          <SelkirkIcon name={ICON.uiPointerHint} size={20} decorative />
          <span>
            <strong style={{ color: '#06b6d4' }}>Click any data point</strong> to investigate and filter the table below
          </span>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ControlStandardsChart;

